import type { MouseEvent } from 'react';
import React, { useMemo } from 'react';
import { Modal } from 'is24-corecss';
import { Benefit } from '../../../../../@types/benefits';
import type { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { HowItWorksTab } from './components/HowItWorksTab';
import { getBillCheckDropOffStep, isBillCheckV3FlowStep } from './utils/flow-utils';
import { useBillCheckV3 } from './hooks/useBillCheckV3.hook';
import { OverviewTab } from './components/OverviewTab/OverviewTab';
import BillCheckJpg from './images/billcheck.jpeg';
import BillCheckWebp from './images/billcheck.webp';
import BillCheckMobileJpg from './images/billcheckMobile.jpeg';
import BillCheckMobileWebp from './images/billcheckMobile.webp';
import MultiStepCreationModal from './components/MultiStepCreationModal/MultiStepCreationModal';
import { useIntl } from '@/i18n/hooks/useIntl';
import { useMFENavigation } from '@/customHooks/useMFENavigation.hook';
import ModalV2 from '@/components/common/display/modal/ModalV2';
import { BILLCHECK_V3_STATUS } from '@/@types/billCheckV3';
import { useGetBillCheckDetails, useStartBillCheckProcess } from '@/apiHooks/billCheckV3.hook';
import { campaigns } from '@/components/common/campaigns';
import { useUrlState } from '@/customHooks/useUrlState.hook';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { useReportTrackingTenant } from '@/customHooks/useReportTrackingTenant.hook';

export const BillCheckV3 = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const { canStartNewBillCheck, lastBillCheck, isEntitled } = useBillCheckV3();
  const { startBillCheck, isBillCheckStarting } = useStartBillCheckProcess();
  const { navigateToNestedModalPath, nestedModalPath } = useMFENavigation();
  const { currentUrl, isMFE } = useUrlState();
  const { billCheckDetails, isBillCheckDetailsSuccess } = useGetBillCheckDetails(lastBillCheck?.billCheckFlowId);
  const { reportTenantTracking } = useReportTrackingTenant();

  const handleLinkClick = () => {
    if (isMFE) {
      reportTenantTracking({ ...TrackingEvents.TENANT.DISCOVER_WOHNEN });
    }
    window.location.href = `${campaigns[isMFE ? 'BILLCHECK_MFE' : 'BILLCHECK']}${isMFE && currentUrl ? `&returnUrl=${encodeURIComponent(currentUrl)}` : ''}`;
  };

  const { ctaMessageKey, defaultMessageValue, onClick } = useMemo(() => {
    if (!isEntitled) {
      return {
        ctaMessageKey: `common.buttons.discoverWohnenPlus`,
        defaultMessageValue: 'WohnenPlus entdecken',
        onClick: handleLinkClick,
      };
    }

    if (canStartNewBillCheck) {
      return {
        ctaMessageKey: 'benefits.billCheckV3.modal.buttonCta.startBillcheck',
        defaultMessageValue: 'Nebenkosten-Check starten',
        onClick: (e: MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          startBillCheck();
        },
      };
    }

    if (lastBillCheck?.billCheckStatus === BILLCHECK_V3_STATUS.STARTED && isBillCheckDetailsSuccess) {
      return {
        ctaMessageKey: 'benefits.billCheckV3.modal.buttonCta.continueBillCheck',
        defaultMessageValue: 'Nebenkosten-Check fortsetzen',
        onClick: () => navigateToNestedModalPath(getBillCheckDropOffStep(billCheckDetails!)),
      };
    }

    return {
      ctaMessageKey: null,
      defaultMessageValue: null,
      onClick: null,
    };
  }, [
    lastBillCheck,
    isEntitled,
    isBillCheckDetailsSuccess,
    billCheckDetails,
    startBillCheck,
    navigateToNestedModalPath,
    canStartNewBillCheck,
    handleLinkClick,
  ]);

  const shouldShowPrimaryButton = ctaMessageKey && defaultMessageValue;

  if (isEntitled && isBillCheckV3FlowStep(nestedModalPath)) {
    return <MultiStepCreationModal open={true} onClose={onClose} />;
  }

  return (
    <ModalV2
      title={t({ id: 'benefits.billCheckV3.headline', defaultMessage: 'BillCheck' })}
      isVisible={isVisible}
      image={BillCheckJpg}
      imageWebp={BillCheckWebp}
      imageMobile={BillCheckMobileJpg}
      imageMobileWebp={BillCheckMobileWebp}
      imageUseFullWidth={true}
      imageAlt={t({
        id: 'benefits.billCheckV3.modal.image',
        defaultMessage: 'Paper, pencil and calculator',
      })}
      tabsContent={[<OverviewTab isEntitled={isEntitled} key='overview' />, <HowItWorksTab key='howItWorks' />]}
      benefit={Benefit.BILLCHECK}
      onClose={onClose}
      primaryFooterBtn={
        shouldShowPrimaryButton ? (
          <Modal.PrimaryFooterButton
            disabled={isBillCheckStarting}
            label={t({ id: ctaMessageKey!, defaultMessage: defaultMessageValue! })}
            onClick={onClick!}
          />
        ) : undefined
      }
    />
  );
};

export default BillCheckV3;
